// src/components/refundsMoney.js
// The money of the refunds pages, derived from GET /api/logistics/refunds/stats
// (Backend app/api/logistics.py::_build_refund_stats + refund_service.add_to_stats):
// what was granted (approved / processing / paid), what is already paid, what
// is still to collect, what waits for the carrier's decision, and how the
// carriers decide. Pure functions — no fetch, no React — tested in
// refundsMoney.test.js. Amounts are taken from ONE currency bucket: the only
// currency, or the dominant one when the account mixes currencies (the page
// says so). Counts are dossiers across every currency.

import { GRANTED_STATUSES, REFUND_STATUSES } from '../utils/refunds';
import { formatShortDate, formatShortDateTime } from '../utils/dates';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// The currency the money tiles speak: { currency, money, multiCurrency, otherCurrencies }.
export const moneyScope = (stats) => {
  const entries = Object.entries(stats?.by_currency || {})
    .filter(([, bucket]) => bucket && num(bucket.count) > 0)
    .sort((a, b) => num(b[1].count) - num(a[1].count));
  const multiCurrency = entries.length > 1;
  return {
    currency: entries[0]?.[0] || 'EUR',
    money: multiCurrency ? entries[0][1] : (stats?.summary || null),
    multiCurrency,
    otherCurrencies: entries.slice(1).map(([code]) => code),
  };
};

const statusCount = (stats, status) => num(stats?.by_status?.[status]?.count);

// The headline numbers. null when the API gave no stats (no tile is drawn).
export const refundMoneyMetrics = (stats) => {
  const summary = stats?.summary;
  if (!summary) return null;
  const { currency, money, multiCurrency, otherCurrencies } = moneyScope(stats);
  const granted = num(money?.granted_refund_amount);
  const paid = num(money?.paid_refund_amount);
  const grantedCount = num(summary.granted_count);
  const rejectedCount = num(summary.rejected_count);
  const decided = grantedCount + rejectedCount;
  const byField = money?.granted_by_field || {};
  const composition = [
    { key: 'shipping', value: num(byField.shipping_cost_refund_amount) },
    { key: 'content', value: num(byField.content_refund_amount) },
    { key: 'insurance', value: num(byField.insurance_refund_amount) },
  ].filter((part) => part.value > 0);
  const known = composition.reduce((sum, part) => sum + part.value, 0);
  // Amounts typed as a total only (older refunds): the rest of the granted money.
  if (granted > 0 && (granted - known) / granted > 0.01) composition.push({ key: 'other', value: round2(granted - known) });
  return {
    currency,
    multiCurrency,
    otherCurrencies,
    count: num(summary.count),
    granted,
    grantedCount,
    paid,
    paidCount: statusCount(stats, 'paid'),
    paidShare: granted > 0 ? paid / granted : null,
    toCollect: round2(Math.max(0, granted - paid)),
    toCollectCount: statusCount(stats, 'approved') + statusCount(stats, 'processing'),
    requestedCount: statusCount(stats, 'requested'),
    rejectedCount,
    cancelledCount: statusCount(stats, 'cancelled'),
    acceptanceRate: decided > 0 ? grantedCount / decided : null,
    amountUnknown: num(summary.amount_unknown_count),
    aiCount: num(summary.ai_recorded_count),
    aiShare: num(summary.count) > 0 ? num(summary.ai_recorded_count) / num(summary.count) : null,
    average: num(money?.average_refund_amount),
    insurancePrice: num(money?.insurance_price),
    insuredCount: num(summary.insured_refunds_count),
    composition,
  };
};

function round2(value) {
  return Math.round(value * 100) / 100;
}

// One row per status in the refund's forward order, with its dossiers and money.
export const statusRows = (stats) => REFUND_STATUSES.map((status) => {
  const bucket = stats?.by_status?.[status];
  return {
    status,
    count: num(bucket?.count),
    amount: num(bucket?.total_refund_amount),
    granted: GRANTED_STATUSES.includes(status),
  };
});

// A breakdown (by_carrier, by_client) as rows sorted by granted money then
// dossiers; past `limit`, the rest folds into one « others » line.
export const breakdownRows = (container, limit = 6) => {
  const rows = Object.entries(container || {})
    .map(([label, bucket]) => {
      const grantedCount = num(bucket?.granted_count);
      const rejectedCount = num(bucket?.rejected_count);
      const decided = grantedCount + rejectedCount;
      return {
        label,
        unknown: label === 'Unknown',
        count: num(bucket?.count),
        granted: num(bucket?.granted_refund_amount),
        paid: num(bucket?.paid_refund_amount),
        grantedCount,
        rejectedCount,
        acceptanceRate: decided > 0 ? grantedCount / decided : null,
      };
    })
    .sort((a, b) => b.granted - a.granted || b.count - a.count || a.label.localeCompare(b.label));
  const top = rows.slice(0, limit);
  const rest = rows.slice(limit);
  const others = rest.length
    ? {
      labels: rest.length,
      count: rest.reduce((sum, row) => sum + row.count, 0),
      granted: round2(rest.reduce((sum, row) => sum + row.granted, 0)),
    }
    : null;
  return { rows: top, others, max: Math.max(0, ...rows.map((row) => row.granted)) };
};

// What a list row shows in its amount cell: the money, « to confirm » (granted
// but the carrier has not given the amount yet), or nothing (a request, a
// refusal, a cancellation carry no money).
export const amountState = (refund) => {
  if (num(refund?.total_refund_amount) > 0) return 'amount';
  if (GRANTED_STATUSES.includes(refund?.status)) return 'pending';
  return 'none';
};

// Badge tone of a refund status: a request waits (info), a decision is the
// accent, money on its way is warning, paid is success, a refusal is danger.
export const STATUS_TONE = {
  requested: 'info', approved: 'accent', processing: 'warning', paid: 'success', rejected: 'danger', cancelled: undefined,
};
// A live state carries the badge dot; a final one does not.
export const LIVE_STATUSES = ['requested', 'approved', 'processing'];

// ---------------------------------------------------------------- formatting

const localeOf = (lang) => {
  const code = String(lang || 'fr').slice(0, 2);
  return { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' }[code] || lang;
};

// « 1 671,50 € ». whole: no cents (tiles over 1 000, full value in the title).
export const formatAmount = (amount, currency = 'EUR', lang = 'fr', { whole = false } = {}) => {
  const digits = whole ? 0 : 2;
  try {
    return new Intl.NumberFormat(localeOf(lang), {
      style: 'currency', currency: currency || 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(num(amount));
  } catch (e) {
    return `${num(amount).toFixed(digits)} ${currency || ''}`.trim();
  }
};

export const formatTileAmount = (amount, currency, lang) => formatAmount(amount, currency, lang, { whole: num(amount) >= 1000 });

export const formatPercent = (ratio, lang = 'fr') => new Intl.NumberFormat(localeOf(lang), {
  style: 'percent', maximumFractionDigits: 0,
}).format(num(ratio));

export const formatCount = (value, lang = 'fr') => num(value).toLocaleString(localeOf(lang));

// Dates: the shared short format (utils/dates — parseUtc, Paris time).
// A calendar day the user picked (« 2026-10-28 » → « 28 oct. », « 28 oct. 2025 » another year).
export const formatDay = (value, lang) => (/^\d{4}-\d{2}-\d{2}$/.test(value || '')
  ? formatShortDate(value, lang, { empty: '' })
  : '');

// A backend timestamp (naive UTC) in Paris time: « 27 oct. » or « 27 oct., 09:30 ».
export const formatStamp = (value, lang, { time = false } = {}) => (time
  ? formatShortDateTime(value, lang)
  : formatShortDate(value, lang));

// ---------------------------------------------------------------- period

export const PERIOD_PRESETS = [30, 90, 365];

const pad = (n) => String(n).padStart(2, '0');
export const isoDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const presetStart = (days, today = new Date()) => {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - days);
  return isoDay(start);
};

// 'all' | '30' | '90' | '365' | 'custom' from the URL's start / end.
export const periodOf = (start, end, today = new Date()) => {
  if (!start && !end) return 'all';
  if (!end) {
    const preset = PERIOD_PRESETS.find((days) => presetStart(days, today) === start);
    if (preset) return String(preset);
  }
  return 'custom';
};

// The API reads end_date as a datetime: a bare day would stop at 00:00 and
// leave that day's refunds out. The chosen day is included.
export const endOfDay = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(value || '') ? `${value}T23:59:59` : value);
